import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { ArrowUpLeft, ChevronsDown, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import MainButton from "../../components/MainButton";
import { motion } from "framer-motion";
import { Card } from "src/components/ui/card";
import { cn } from "../../lib/utils";
import { CourseCard } from "../../components/CourseCard";
import { useAPI } from "../../context/APIContext";
import { SectionTitle } from "./_components/SectionTitle";
import { CustomAccordion } from "./_components/CustomAccordion";
import { CustomCarousel } from "./_components/CustomCarousel";
import { CustomCard } from "./_components/CustomCard";
import {
  accordingData,
  CoursesData,
  DiscoverData,
  ReviewData,
} from "./_components/demo-data";
import TeacherCard from "./_components/TeacherCard";
import { useAuth } from "../../context/AuthContext";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../lib/api";
import { toast } from "sonner";
import axios from "axios";
import { Skeleton } from "src/components/ui/skeleton";
import LoaderPage from "../../components/LoaderPage";

function Marquee({ title }) {
  return (
    <div className="relative w-full overflow-hidden bg-background py-16">
      <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background z-10" />
      <motion.div
        className="flex whitespace-nowrap"
        animate={{ x: ["0%", "-50%"] }}
        transition={{
          repeat: Number.POSITIVE_INFINITY,
          ease: "linear",
          duration: 20,
        }}
      >
        {[...Array(4)].map((_, index) => (
          <div key={index} className="flex items-center mx-4">
            <span
              className="text-7xl sm:text-8xl md:text-9xl font-bold text-transparent px-4"
              style={{
                WebkitTextStroke: "1px rgb(156 163 175)", // tailwind gray-400
              }}
            >
              {title}
            </span>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function FloatingPaths({ position }) {
  const paths = Array.from({ length: 36 }, (_, i) => ({
    id: i,
    d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${
      380 - i * 5 * position
    } -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${
      152 - i * 5 * position
    } ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${
      684 - i * 5 * position
    } ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
    color: `rgba(15,23,42,${0.1 + i * 0.03})`,
    width: 0.5 + i * 0.03,
  }));

  return (
    <div className="absolute inset-0 pointer-events-none">
      <svg
        className="w-full h-full text-slate-950 dark:text-white"
        viewBox="0 0 696 316"
        fill="none"
      >
        <title>Background Paths</title>
        {paths.map((path) => (
          <motion.path
            key={path.id}
            d={path.d}
            stroke="currentColor"
            strokeWidth={path.width}
            strokeOpacity={0.1 + path.id * 0.03}
            initial={{ pathLength: 0.3, opacity: 0.6 }}
            animate={{
              pathLength: 1,
              opacity: [0.3, 0.6, 0.3],
              pathOffset: [0, 1, 0],
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Number.POSITIVE_INFINITY,
              ease: "linear",
            }}
          />
        ))}
      </svg>
    </div>
  );
}

export default function MainHome() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin"; // Check if the user is an admin
  const isTeacher = user?.role === "teacher"; // Check if the user is an admin

  const [activeFeatures, setActiveFeatures] = useState(1);

  const { coursesItems, categoriesItems } = useAPI();
  const { loadingCourses, courses, getAllCourses } = coursesItems;
  const { categories, loadingCategories, getAllCategories } = categoriesItems;

  // Main Section
  const [mainSection, setMainSection] = useState([]);
  const [loadingMainSection, setLoadingMainSection] = useState(true);
  const getAllMainSection = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/main-sections`);
      setMainSection(data.data[data.data.length - 1]);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingMainSection(false);
    }
  };

  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const fetchTeachers = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/users`);
      setTeachers(data.data.filter((user) => user?.role === "teacher"));
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoadingTeachers(false);
    }
  };

  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/reviews/site`);
      setReviews(data.data);
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string")
        toast("Error", { description: msg, status: "error" });
    } finally {
      setLoadingReviews(false);
    }
  };

  useEffect(() => {
    getAllMainSection();
    fetchTeachers();
    fetchReviews();
  }, []);

  const [email, setEmail] = useState("");

  if (
    loadingMainSection ||
    loadingTeachers ||
    loadingReviews ||
    loadingCourses ||
    loadingCategories
  ) {
    return <LoaderPage title={"Loading Data"} />;
  }

  return (
    <div
      className="relative mt-10 md:mt-0"
      dir={language === "en" ? "ltr" : "rtl"}
    >
      <div className="space-y-28">
        {/* Main */}
        <main className="relative">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 items-center gap-6">
            {loadingMainSection ? (
              <div className="space-y-8 my-32">
                {/* Title Skeleton */}
                <div className="space-y-2">
                  <Skeleton className="h-10 w-3/4" />
                  <Skeleton className="h-10 w-1/2" />
                  <Skeleton className="h-10 w-1/3" />
                </div>

                {/* Description Skeleton */}
                <Skeleton className="h-6 lg:h-7 w-2/3" />

                {/* Buttons Skeleton */}
                <div className="flex flex-wrap gap-3">
                  <Skeleton className="h-10 w-32 rounded-md" />
                  <Skeleton className="h-10 w-32 rounded-md" />
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="text-4xl lg:text-7xl font-bold drop-shadow-md leading-tight">
                  {mainSection.main_title}{" "}
                  <span className="bg-gradient-to-r from-blue-400 to-blue-700 bg-clip-text text-transparent">
                    {mainSection.highlight_1} {mainSection.highlight_2}
                  </span>
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 50 50"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline-block"
                  >
                    <path
                      d="M30.4691 40.625H37.766C38.0593 40.6249 38.3466 40.5423 38.5951 40.3866C38.8436 40.2309 39.0433 40.0084 39.1713 39.7445L45.937 25.8041C46.0394 25.593 46.093 25.3616 46.0937 25.1271C46.0945 24.8925 46.0425 24.6608 45.9415 24.4491L39.1759 10.2645C39.0488 9.99829 38.849 9.77346 38.5995 9.61604C38.35 9.45863 38.061 9.37506 37.766 9.375H30.4691C30.2071 9.37895 29.9503 9.44807 29.7217 9.57613C29.4931 9.70419 29.2999 9.88715 29.1597 10.1085C29.0195 10.3299 28.9367 10.5826 28.9186 10.844C28.9005 11.1054 28.9478 11.3672 29.0562 11.6058L35.5015 25.2319L29.0654 38.3758C28.9532 38.6144 28.9026 38.8774 28.9183 39.1406C28.9339 39.4038 29.0152 39.6589 29.1548 39.8826C29.2944 40.1063 29.4879 40.2914 29.7175 40.4211C29.9471 40.5508 30.2055 40.6209 30.4691 40.625ZM36.7877 37.5H32.9752L38.6393 25.9308C38.7424 25.7201 38.7968 25.4889 38.7984 25.2543C38.8 25.0198 38.7488 24.7878 38.6485 24.5758L32.9365 12.5H36.7802L42.7982 25.1159L36.7877 37.5ZM17.9691 40.625H25.266C25.5593 40.6249 25.8466 40.5423 26.0951 40.3866C26.3436 40.2309 26.5433 40.0084 26.6713 39.7445L33.437 25.8041C33.5394 25.593 33.593 25.3616 33.5937 25.1271C33.5945 24.8925 33.5425 24.6608 33.4415 24.4491L26.6759 10.2645C26.5488 9.99829 26.349 9.77346 26.0995 9.61604C25.85 9.45863 25.561 9.37506 25.266 9.375H17.9691C17.7071 9.37895 17.4503 9.44807 17.2217 9.57613C16.9931 9.70419 16.8 9.88715 16.6597 10.1085C16.5195 10.3299 16.4367 10.5826 16.4186 10.844C16.4005 11.1054 16.4478 11.3672 16.5562 11.6058L23.0015 25.2319L16.5654 38.3758C16.4532 38.6144 16.4027 38.8774 16.4183 39.1406C16.4339 39.4038 16.5152 39.6589 16.6548 39.8826C16.7945 40.1063 16.9879 40.2914 17.2175 40.4211C17.4471 40.5508 17.7055 40.6209 17.9691 40.625ZM24.2877 37.5H20.4752L26.1393 25.9308C26.2424 25.7201 26.2968 25.4889 26.2984 25.2543C26.3 25.0198 26.2488 24.7878 26.1485 24.5758L20.4365 12.5H24.2802L30.2984 25.1159L24.2877 37.5ZM5.46914 40.625H12.766C13.0593 40.6249 13.3466 40.5423 13.5951 40.3866C13.8436 40.2309 14.0433 40.0084 14.1713 39.7445L20.937 25.8041C21.0394 25.593 21.093 25.3616 21.0937 25.1271C21.0945 24.8925 21.0425 24.6608 20.9415 24.4491L14.1759 10.2645C14.0488 9.99829 13.849 9.77346 13.5995 9.61604C13.35 9.45863 13.061 9.37506 12.766 9.375H5.46914C5.20715 9.37895 4.95026 9.44807 4.72166 9.57613C4.49307 9.70419 4.29995 9.88715 4.15974 10.1085C4.01954 10.3299 3.93665 10.5826 3.91858 10.844C3.90049 11.1054 3.94778 11.3672 4.05618 11.6058L10.5015 25.2319L4.0654 38.3758C3.95323 38.6144 3.90265 38.8774 3.91828 39.1406C3.9339 39.4038 4.01524 39.6589 4.15484 39.8826C4.29445 40.1063 4.48788 40.2914 4.71748 40.4211C4.94706 40.5508 5.20549 40.6209 5.46914 40.625ZM11.7877 37.5H7.97524L13.6393 25.9308C13.7425 25.7201 13.7968 25.4889 13.7984 25.2543C13.8 25.0198 13.7488 24.7878 13.6485 24.5758L7.93649 12.5H11.7802L17.7984 25.1159L11.7877 37.5Z"
                      fill="url(#paint0_linear_8_15560)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_8_15560"
                        x1="46.0938"
                        y1="25"
                        x2="3.90977"
                        y2="25"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#059CF8" />
                        <stop offset="1" stop-color="#1F59D0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="text-lg lg:text-xl font-medium drop-shadow-lg">
                  {mainSection.description}
                </div>
                <div className="flex flex-wrap gap-3">
                  <MainButton
                    title={mainSection.primary_button_text}
                    onClick={() =>
                      navigate(
                        user
                          ? isAdmin
                            ? "/admin/overview"
                            : isTeacher
                            ? "/teacher/courses"
                            : "/dashboard"
                          : mainSection.primary_button_link
                      )
                    }
                  />
                  <MainButton
                    title={mainSection.secondary_button_text}
                    className={
                      "bg-transparent text-primary border-2 border-blue-600 shadow-sm"
                    }
                    Icon={ArrowUpLeft}
                    onClick={() => navigate(mainSection.secondary_button_link)}
                  />
                </div>
              </div>
            )}

            {/* Image */}
            <div className="h-full md:flex items-center justify-center hidden">
              <img
                loading="lazy"
                src={`${UrlImage}${mainSection.image}`}
                alt="student"
                className="object-cover rounded-xl"
              />
            </div>
          </div>
          {/* Animation Background */}
          <div className="absolute inset-0 -z-10">
            <FloatingPaths position={1} />
            <FloatingPaths position={-1} />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 from-90% to-background"></div>
          </div>

          {/* Btn Down */}
          <div className="absolute inset-0 pointer-events-none flex items-end justify-center w-full !-mb-14 md:mb-5">
            <ChevronsDown className="size-8 animate-bounce" />
          </div>
        </main>

        {/* Courses */}
        {categories.length !== 0 && (
          <section className="space-y-10 max-w-7xl mx-auto px-4">
            <div className="space-y-4 text-center">
              <SectionTitle
                title={"اكتشف ما يزيد علي 100 دورة تعليمية في عدة مجالات"}
              />
              <p className="text-lg text-muted-foreground">اكتشف جميع الفئات</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {loadingCategories
                ? Array(3)
                    .fill()
                    .map((_, index) => (
                      <Skeleton key={index} className="w-full h-40" />
                    ))
                : categories.map((industry, index) => (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.2 }}
                      className="group relative"
                    >
                      <CustomCard
                        key={index}
                        name={industry.title[language]}
                        description={industry.description[language]}
                        image={industry.image}
                      />
                    </motion.div>
                  ))}
            </div>
          </section>
        )}

        {/* Adviser */}
        <Card className="px-4 bg-gradient-to-r from-blue-600 to-blue-500 relative overflow-hidden rounded-none">
          <div className="max-w-7xl mx-auto p-6">
            {/* Decorative elements */}
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-blue-400 opacity-20"></div>
            <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-blue-700 opacity-20"></div>

            <div className="relative flex flex-col items-center justify-between gap-4 md:flex-row">
              <div className="text-center md:text-right">
                <h3 className="mb-4 text-xl font-bold text-white">
                  للإعلان هنا يرجى التواصل معنا :
                </h3>
                <div className="flex flex-col items-center gap-2 text-white md:items-start">
                  <div className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    <span className="font-medium">+201234567890</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    <span className="font-medium">example@gmail.com</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 md:mt-0">
                <Link
                  href="#contact"
                  className="group relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-white px-8 py-3 font-bold text-blue-600 shadow-md transition duration-300 ease-out hover:scale-105"
                >
                  <span className="relative">إعلانك هنا</span>
                  <span className="absolute bottom-0 left-0 h-1 w-0 bg-blue-800 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </div>
            </div>
          </div>
        </Card>

        {/* Features */}
        <section className="space-y-10 max-w-7xl mx-auto px-4">
          <SectionTitle title={"مميزات دوراتنا"} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 px-10">
            {[
              "الدورات المباشرة",
              "الملفات المتاحة",
              "الاختبارات الدورية / الامتحانات",
              "الدعم الفني 24/7",
            ].map((item, index) => (
              <div
                onClick={() => setActiveFeatures(index + 1)}
                className={cn(
                  "rounded-full text-white px-4 py-2 text-center cursor-pointer transition-colors duration-300 bg-gradient-to-tr from-blue-400 to-blue-700",
                  activeFeatures === index + 1
                    ? ""
                    : "border-2 border-blue-600 bg-clip-text text-transparent"
                )}
              >
                {index + 1}. {item}
              </div>
            ))}
          </div>

          <motion.div
            key={activeFeatures}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            <div className="h-[400px] md:h-auto lg:h-[400px] col-span-1">
              <img
                loading="lazy"
                src="features.jpg"
                alt="features"
                className="object-cover object-bottom w-full h-full rounded-2xl"
              />
            </div>
            <Card className="p-6 flex flex-col justify-between gap-6 rounded-2xl col-span-1 lg:col-span-2">
              <div className="text-2xl font-bold">الدورات المباشرة</div>

              <div className="flex items-center gap-6">
                <div className="">
                  <svg
                    width="40"
                    height="40"
                    flex-shrink="0"
                    viewBox="0 0 47 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M25.8723 13.8438C25.8723 13.1888 25.4636 12.6032 24.8488 12.3772C24.2338 12.1513 23.5433 12.3329 23.1191 12.8321L13.7781 23.8254C13.3838 24.2895 13.2949 24.9404 13.5506 25.4931C13.8063 26.0458 14.3598 26.3996 14.9688 26.3996H18.6373V35.7188C18.6373 36.3783 19.0512 36.9667 19.6719 37.1895C20.2925 37.4123 20.9862 37.2214 21.4056 36.7125L33.3621 22.2036C33.7467 21.7369 33.8274 21.0902 33.5694 20.5433C33.3114 19.9963 32.761 19.6474 32.1563 19.6474H25.8723V13.8438ZM20.1998 23.2746H18.3468L22.7473 18.0958V21.2099C22.7473 22.0728 23.4469 22.7724 24.3098 22.7724H28.8439L21.7623 31.3659V24.8371C21.7623 23.9742 21.0627 23.2746 20.1998 23.2746Z"
                      fill="#5A00D1"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M19.9845 47.4375C21.3753 47.4375 22.6371 46.8852 23.5625 45.9879C24.4879 46.8852 25.7497 47.4375 27.1405 47.4375C28.0316 47.4375 28.9034 47.3181 29.737 47.0919C30.5059 47.317 31.3181 47.4375 32.1562 47.4375C36.3594 47.4375 39.8567 44.4209 40.603 40.4338C42.5519 39.3756 43.875 37.3111 43.875 34.9375C43.875 33.8659 43.6046 32.8566 43.1293 31.9751C44.5369 30.8298 45.4375 29.0837 45.4375 27.125C45.4375 25.9374 45.1055 24.8267 44.5307 23.8815C46.0407 22.5928 47 20.675 47 18.5312C47 15.4643 45.0371 12.8569 42.2984 11.8954C42.3077 11.7647 42.3125 11.6329 42.3125 11.5C42.3125 8.73031 40.2535 6.44146 37.5826 6.08074C37.2203 2.97377 34.5792 0.5625 31.375 0.5625C30.7428 0.5625 30.1305 0.656836 29.5524 0.832852C28.8007 0.655375 28.0215 0.5625 27.2277 0.5625C25.8 0.5625 24.5059 1.13484 23.5625 2.0626C22.6191 1.13484 21.325 0.5625 19.8973 0.5625C19.1035 0.5625 18.3243 0.655375 17.5726 0.832852C16.9945 0.656836 16.3822 0.5625 15.75 0.5625C15.6836 0.5625 15.6174 0.563539 15.5516 0.565594C15.4877 0.563539 15.4237 0.5625 15.3594 0.5625C12.2363 0.5625 9.68391 3.00587 9.50953 6.08528C6.85461 6.46027 4.8125 8.74164 4.8125 11.5C4.8125 11.6327 4.81726 11.7645 4.82662 11.8952C2.08833 12.8563 0.125 15.4645 0.125 18.5312C0.125 19.0632 0.18407 19.5812 0.295961 20.0792C0.634797 21.5873 1.45823 22.9123 2.59416 23.8817C2.01941 24.827 1.6875 25.9375 1.6875 27.125C1.6875 29.0837 2.58809 30.8298 3.99571 31.9751C3.52039 32.8566 3.25 33.8659 3.25 34.9375C3.25 35.1084 3.25685 35.2775 3.27029 35.4448C3.33076 36.1979 3.52466 36.913 3.82821 37.5666C3.86195 37.6392 3.89702 37.7111 3.93343 37.7822C4.50877 38.9058 5.41431 39.8323 6.52203 40.4338C7.26828 44.4209 10.7656 47.4375 14.9688 47.4375C15.8069 47.4375 16.6191 47.317 17.388 47.0919C18.2216 47.3181 19.0934 47.4375 19.9845 47.4375ZM15.5481 3.69391C13.9163 3.79795 12.625 5.15442 12.625 6.8125C12.625 7.01422 12.6439 7.21008 12.6796 7.39891C12.7775 7.91602 12.6084 8.44773 12.2298 8.81336C11.8512 9.17898 11.3141 9.32945 10.8006 9.21367C10.635 9.17633 10.4614 9.15625 10.2812 9.15625C8.9868 9.15625 7.9375 10.2055 7.9375 11.5C7.9375 11.5725 7.94078 11.6441 7.94711 11.7146C8.40883 11.9862 8.71875 12.4881 8.71875 13.0625C8.71875 13.9255 8.01922 14.625 7.15625 14.625C6.00473 14.625 4.96959 15.1233 4.25466 15.9159C3.71388 16.5155 3.35631 17.2836 3.27016 18.1319C3.25683 18.2632 3.25 18.3964 3.25 18.5312C3.25 18.8279 3.28308 19.117 3.34578 19.3949C3.73888 21.1367 5.29566 22.4375 7.15625 22.4375C8.01922 22.4375 8.71875 23.137 8.71875 24C8.71875 24.863 8.01922 25.5625 7.15625 25.5625C6.54094 25.5625 5.94411 25.4834 5.37533 25.335C5.01983 25.8425 4.8125 26.4592 4.8125 27.125C4.8125 28.4632 5.65437 29.6087 6.84266 30.0533C7.34883 30.2427 7.72125 30.6802 7.82727 31.2102C7.93328 31.7401 7.75797 32.2872 7.36367 32.6568C6.75313 33.229 6.375 34.0378 6.375 34.9375C6.375 36.6634 7.77414 38.0625 9.5 38.0625C11.2259 38.0625 12.625 36.6634 12.625 34.9375C12.625 34.0745 13.3245 33.375 14.1875 33.375C15.0505 33.375 15.75 34.0745 15.75 34.9375C15.75 38.2155 13.2264 40.9041 10.0161 41.1665C10.8895 43.0252 12.7791 44.3125 14.9688 44.3125C15.627 44.3125 16.2548 44.1969 16.8351 43.9863C17.1648 43.8666 17.5251 43.8612 17.8581 43.9712C18.5316 44.1936 19.2474 44.3125 19.9845 44.3125C21.0977 44.3125 22 43.4102 22 42.297V38.0625C22 37.1995 22.6995 36.5 23.5625 36.5C24.4255 36.5 25.125 37.1995 25.125 38.0625V42.297C25.125 43.4102 26.0273 44.3125 27.1405 44.3125C27.8776 44.3125 28.5934 44.1936 29.2669 43.9712C29.5999 43.8612 29.9602 43.8666 30.2899 43.9863C30.8702 44.1969 31.498 44.3125 32.1562 44.3125C34.3459 44.3125 36.2355 43.0252 37.1089 41.1665C33.8986 40.9041 31.375 38.2155 31.375 34.9375C31.375 34.0745 32.0745 33.375 32.9375 33.375C33.8005 33.375 34.5 34.0745 34.5 34.9375C34.5 36.6634 35.8991 38.0625 37.625 38.0625C38.8673 38.0625 39.9403 37.3376 40.4441 36.2877C40.5964 35.9702 40.6967 35.6229 40.7338 35.257C40.7445 35.152 40.75 35.0454 40.75 34.9375C40.75 34.0378 40.3719 33.229 39.7613 32.6568C39.367 32.2872 39.1917 31.7401 39.2977 31.2102C39.4038 30.6802 39.7762 30.2427 40.2823 30.0533C41.4706 29.6087 42.3125 28.4632 42.3125 27.125C42.3125 26.1623 41.8791 25.3024 41.1911 24.7267C40.7777 24.3808 40.5718 23.8463 40.6463 23.3124C40.7208 22.7786 41.0652 22.3209 41.5574 22.1013C42.9261 21.491 43.875 20.1202 43.875 18.5312C43.875 16.4824 42.2966 14.8007 40.2906 14.638C39.7861 14.597 39.3327 14.3142 39.074 13.8791C38.8152 13.4441 38.7834 12.9105 38.9884 12.4477C39.1159 12.1599 39.1875 11.8404 39.1875 11.5C39.1875 10.2055 38.1382 9.15625 36.8438 9.15625C36.6636 9.15625 36.49 9.17633 36.3244 9.21367C35.8109 9.32945 35.2738 9.17898 34.8952 8.81336C34.5166 8.44773 34.3475 7.91602 34.4454 7.39891C34.4811 7.21008 34.5 7.01422 34.5 6.8125C34.5 5.08661 33.1009 3.6875 31.375 3.6875C30.9579 3.6875 30.5636 3.76838 30.204 3.91385C29.8786 4.04552 29.5183 4.06375 29.1812 3.96561C28.557 3.78384 27.9006 3.6875 27.2277 3.6875C26.0664 3.6875 25.125 4.62893 25.125 5.79023V9.9375C25.125 10.8005 24.4255 11.5 23.5625 11.5C22.6995 11.5 22 10.8005 22 9.9375V5.79023C22 4.87753 21.4185 4.10065 20.6058 3.80986C20.998 4.59628 21.2188 5.48331 21.2188 6.42188C21.2188 9.65789 18.5954 12.2812 15.3594 12.2812C14.4964 12.2812 13.7969 11.5817 13.7969 10.7188C13.7969 9.85578 14.4964 9.15625 15.3594 9.15625C16.8695 9.15625 18.0938 7.93203 18.0938 6.42188C18.0938 4.97517 16.9702 3.79088 15.5481 3.69391Z"
                      fill="url(#paint0_linear_11_15756)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_11_15756"
                        x1="5.98438"
                        y1="0.5625"
                        x2="36.0625"
                        y2="39.625"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0.0721154" stop-color="#AD2EDB" />
                        <stop offset="1" stop-color="#039BF0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <div className="text-xl">
                  بالإضافة إلى التدريس المباشر من قبل المعلمين، تُشجع الجلسات
                  الحية على التفاعل بين الطلاب، مما يتيح لهم التعاون في المشاريع
                  والمشاركة في الأنشطة الجماعية في أي وقت.
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 47 47"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M41.0625 0.0625H6.0625C4.48905 0.0666244 2.98121 0.693505 1.86861 1.80611C0.756005 2.91871 0.129124 4.42655 0.125 6V41C0.129124 42.5735 0.756005 44.0813 1.86861 45.1939C2.98121 46.3065 4.48905 46.9334 6.0625 46.9375H41.0625C42.636 46.9334 44.1438 46.3065 45.2564 45.1939C46.369 44.0813 46.9959 42.5735 47 41V6C46.9959 4.42655 46.369 2.91871 45.2564 1.80611C44.1438 0.693505 42.636 0.0666244 41.0625 0.0625ZM6.0625 3.1875H41.0625C41.8072 3.1916 42.5201 3.48924 43.0467 4.0158C43.5733 4.54236 43.8709 5.25534 43.875 6V7.875H3.25V6C3.2541 5.25534 3.55174 4.54236 4.0783 4.0158C4.60486 3.48924 5.31784 3.1916 6.0625 3.1875ZM41.0625 43.8125H6.0625C5.31784 43.8084 4.60486 43.5108 4.0783 42.9842C3.55174 42.4576 3.2541 41.7447 3.25 41V11H43.875V41C43.8709 41.7447 43.5733 42.4576 43.0467 42.9842C42.5201 43.5108 41.8072 43.8084 41.0625 43.8125Z"
                      fill="url(#paint0_linear_11_15751)"
                    />
                    <path
                      d="M23.5625 14.125C20.9357 14.125 18.3679 14.9039 16.1838 16.3633C13.9997 17.8227 12.2975 19.8969 11.2922 22.3237C10.287 24.7506 10.024 27.421 10.5365 29.9973C11.0489 32.5736 12.3138 34.9401 14.1712 36.7975C16.0287 38.6549 18.3952 39.9198 20.9715 40.4323C23.5478 40.9448 26.2182 40.6818 28.645 39.6765C31.0719 38.6713 33.1461 36.969 34.6055 34.7849C36.0648 32.6008 36.8438 30.033 36.8438 27.4062C36.8396 23.8851 35.439 20.5094 32.9492 18.0196C30.4594 15.5297 27.0836 14.1291 23.5625 14.125ZM23.5625 37.5625C21.5538 37.5625 19.5902 36.9668 17.92 35.8509C16.2498 34.7349 14.9481 33.1487 14.1794 31.2929C13.4107 29.4371 13.2095 27.395 13.6014 25.4249C13.9933 23.4547 14.9606 21.6451 16.381 20.2247C17.8013 18.8043 19.611 17.837 21.5811 17.4452C23.5512 17.0533 25.5933 17.2544 27.4491 18.0231C29.3049 18.7918 30.8911 20.0936 32.0071 21.7637C33.1231 23.4339 33.7188 25.3975 33.7188 27.4062C33.7146 30.0986 32.6433 32.6795 30.7395 34.5832C28.8357 36.487 26.2548 37.5584 23.5625 37.5625Z"
                      fill="#2155CD"
                    />
                    <path
                      d="M28.25 26.625H25.125V21.9375C25.125 21.5231 24.9604 21.1257 24.6674 20.8326C24.3743 20.5396 23.9769 20.375 23.5625 20.375C23.1481 20.375 22.7507 20.5396 22.4576 20.8326C22.1646 21.1257 22 21.5231 22 21.9375V28.1875C22 28.6019 22.1646 28.9993 22.4576 29.2924C22.7507 29.5854 23.1481 29.75 23.5625 29.75H28.25C28.6644 29.75 29.0618 29.5854 29.3549 29.2924C29.6479 28.9993 29.8125 28.6019 29.8125 28.1875C29.8125 27.7731 29.6479 27.3757 29.3549 27.0826C29.0618 26.7896 28.6644 26.625 28.25 26.625Z"
                      fill="#2155CD"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_11_15751"
                        x1="-6.51562"
                        y1="48.5313"
                        x2="54.2344"
                        y2="-2.01563"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#039BF0" />
                        <stop offset="1" stop-color="#D94DEB" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <div className="text-xl">
                  تتيح هذه الجلسات المباشرة للمتعلمين التفاعل مباشرةً مع
                  المعلمين الخبراء، والمشاركة في نقاشات تفاعلية، وطرح الأسئلة في
                  الوقت الفعلي.
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="">
                  <svg
                    width="40"
                    height="40"
                    flex-shrink="0"
                    viewBox="0 0 47 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M25.8723 13.8438C25.8723 13.1888 25.4636 12.6032 24.8488 12.3772C24.2338 12.1513 23.5433 12.3329 23.1191 12.8321L13.7781 23.8254C13.3838 24.2895 13.2949 24.9404 13.5506 25.4931C13.8063 26.0458 14.3598 26.3996 14.9688 26.3996H18.6373V35.7188C18.6373 36.3783 19.0512 36.9667 19.6719 37.1895C20.2925 37.4123 20.9862 37.2214 21.4056 36.7125L33.3621 22.2036C33.7467 21.7369 33.8274 21.0902 33.5694 20.5433C33.3114 19.9963 32.761 19.6474 32.1563 19.6474H25.8723V13.8438ZM20.1998 23.2746H18.3468L22.7473 18.0958V21.2099C22.7473 22.0728 23.4469 22.7724 24.3098 22.7724H28.8439L21.7623 31.3659V24.8371C21.7623 23.9742 21.0627 23.2746 20.1998 23.2746Z"
                      fill="#5A00D1"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M19.9845 47.4375C21.3753 47.4375 22.6371 46.8852 23.5625 45.9879C24.4879 46.8852 25.7497 47.4375 27.1405 47.4375C28.0316 47.4375 28.9034 47.3181 29.737 47.0919C30.5059 47.317 31.3181 47.4375 32.1562 47.4375C36.3594 47.4375 39.8567 44.4209 40.603 40.4338C42.5519 39.3756 43.875 37.3111 43.875 34.9375C43.875 33.8659 43.6046 32.8566 43.1293 31.9751C44.5369 30.8298 45.4375 29.0837 45.4375 27.125C45.4375 25.9374 45.1055 24.8267 44.5307 23.8815C46.0407 22.5928 47 20.675 47 18.5312C47 15.4643 45.0371 12.8569 42.2984 11.8954C42.3077 11.7647 42.3125 11.6329 42.3125 11.5C42.3125 8.73031 40.2535 6.44146 37.5826 6.08074C37.2203 2.97377 34.5792 0.5625 31.375 0.5625C30.7428 0.5625 30.1305 0.656836 29.5524 0.832852C28.8007 0.655375 28.0215 0.5625 27.2277 0.5625C25.8 0.5625 24.5059 1.13484 23.5625 2.0626C22.6191 1.13484 21.325 0.5625 19.8973 0.5625C19.1035 0.5625 18.3243 0.655375 17.5726 0.832852C16.9945 0.656836 16.3822 0.5625 15.75 0.5625C15.6836 0.5625 15.6174 0.563539 15.5516 0.565594C15.4877 0.563539 15.4237 0.5625 15.3594 0.5625C12.2363 0.5625 9.68391 3.00587 9.50953 6.08528C6.85461 6.46027 4.8125 8.74164 4.8125 11.5C4.8125 11.6327 4.81726 11.7645 4.82662 11.8952C2.08833 12.8563 0.125 15.4645 0.125 18.5312C0.125 19.0632 0.18407 19.5812 0.295961 20.0792C0.634797 21.5873 1.45823 22.9123 2.59416 23.8817C2.01941 24.827 1.6875 25.9375 1.6875 27.125C1.6875 29.0837 2.58809 30.8298 3.99571 31.9751C3.52039 32.8566 3.25 33.8659 3.25 34.9375C3.25 35.1084 3.25685 35.2775 3.27029 35.4448C3.33076 36.1979 3.52466 36.913 3.82821 37.5666C3.86195 37.6392 3.89702 37.7111 3.93343 37.7822C4.50877 38.9058 5.41431 39.8323 6.52203 40.4338C7.26828 44.4209 10.7656 47.4375 14.9688 47.4375C15.8069 47.4375 16.6191 47.317 17.388 47.0919C18.2216 47.3181 19.0934 47.4375 19.9845 47.4375ZM15.5481 3.69391C13.9163 3.79795 12.625 5.15442 12.625 6.8125C12.625 7.01422 12.6439 7.21008 12.6796 7.39891C12.7775 7.91602 12.6084 8.44773 12.2298 8.81336C11.8512 9.17898 11.3141 9.32945 10.8006 9.21367C10.635 9.17633 10.4614 9.15625 10.2812 9.15625C8.9868 9.15625 7.9375 10.2055 7.9375 11.5C7.9375 11.5725 7.94078 11.6441 7.94711 11.7146C8.40883 11.9862 8.71875 12.4881 8.71875 13.0625C8.71875 13.9255 8.01922 14.625 7.15625 14.625C6.00473 14.625 4.96959 15.1233 4.25466 15.9159C3.71388 16.5155 3.35631 17.2836 3.27016 18.1319C3.25683 18.2632 3.25 18.3964 3.25 18.5312C3.25 18.8279 3.28308 19.117 3.34578 19.3949C3.73888 21.1367 5.29566 22.4375 7.15625 22.4375C8.01922 22.4375 8.71875 23.137 8.71875 24C8.71875 24.863 8.01922 25.5625 7.15625 25.5625C6.54094 25.5625 5.94411 25.4834 5.37533 25.335C5.01983 25.8425 4.8125 26.4592 4.8125 27.125C4.8125 28.4632 5.65437 29.6087 6.84266 30.0533C7.34883 30.2427 7.72125 30.6802 7.82727 31.2102C7.93328 31.7401 7.75797 32.2872 7.36367 32.6568C6.75313 33.229 6.375 34.0378 6.375 34.9375C6.375 36.6634 7.77414 38.0625 9.5 38.0625C11.2259 38.0625 12.625 36.6634 12.625 34.9375C12.625 34.0745 13.3245 33.375 14.1875 33.375C15.0505 33.375 15.75 34.0745 15.75 34.9375C15.75 38.2155 13.2264 40.9041 10.0161 41.1665C10.8895 43.0252 12.7791 44.3125 14.9688 44.3125C15.627 44.3125 16.2548 44.1969 16.8351 43.9863C17.1648 43.8666 17.5251 43.8612 17.8581 43.9712C18.5316 44.1936 19.2474 44.3125 19.9845 44.3125C21.0977 44.3125 22 43.4102 22 42.297V38.0625C22 37.1995 22.6995 36.5 23.5625 36.5C24.4255 36.5 25.125 37.1995 25.125 38.0625V42.297C25.125 43.4102 26.0273 44.3125 27.1405 44.3125C27.8776 44.3125 28.5934 44.1936 29.2669 43.9712C29.5999 43.8612 29.9602 43.8666 30.2899 43.9863C30.8702 44.1969 31.498 44.3125 32.1562 44.3125C34.3459 44.3125 36.2355 43.0252 37.1089 41.1665C33.8986 40.9041 31.375 38.2155 31.375 34.9375C31.375 34.0745 32.0745 33.375 32.9375 33.375C33.8005 33.375 34.5 34.0745 34.5 34.9375C34.5 36.6634 35.8991 38.0625 37.625 38.0625C38.8673 38.0625 39.9403 37.3376 40.4441 36.2877C40.5964 35.9702 40.6967 35.6229 40.7338 35.257C40.7445 35.152 40.75 35.0454 40.75 34.9375C40.75 34.0378 40.3719 33.229 39.7613 32.6568C39.367 32.2872 39.1917 31.7401 39.2977 31.2102C39.4038 30.6802 39.7762 30.2427 40.2823 30.0533C41.4706 29.6087 42.3125 28.4632 42.3125 27.125C42.3125 26.1623 41.8791 25.3024 41.1911 24.7267C40.7777 24.3808 40.5718 23.8463 40.6463 23.3124C40.7208 22.7786 41.0652 22.3209 41.5574 22.1013C42.9261 21.491 43.875 20.1202 43.875 18.5312C43.875 16.4824 42.2966 14.8007 40.2906 14.638C39.7861 14.597 39.3327 14.3142 39.074 13.8791C38.8152 13.4441 38.7834 12.9105 38.9884 12.4477C39.1159 12.1599 39.1875 11.8404 39.1875 11.5C39.1875 10.2055 38.1382 9.15625 36.8438 9.15625C36.6636 9.15625 36.49 9.17633 36.3244 9.21367C35.8109 9.32945 35.2738 9.17898 34.8952 8.81336C34.5166 8.44773 34.3475 7.91602 34.4454 7.39891C34.4811 7.21008 34.5 7.01422 34.5 6.8125C34.5 5.08661 33.1009 3.6875 31.375 3.6875C30.9579 3.6875 30.5636 3.76838 30.204 3.91385C29.8786 4.04552 29.5183 4.06375 29.1812 3.96561C28.557 3.78384 27.9006 3.6875 27.2277 3.6875C26.0664 3.6875 25.125 4.62893 25.125 5.79023V9.9375C25.125 10.8005 24.4255 11.5 23.5625 11.5C22.6995 11.5 22 10.8005 22 9.9375V5.79023C22 4.87753 21.4185 4.10065 20.6058 3.80986C20.998 4.59628 21.2188 5.48331 21.2188 6.42188C21.2188 9.65789 18.5954 12.2812 15.3594 12.2812C14.4964 12.2812 13.7969 11.5817 13.7969 10.7188C13.7969 9.85578 14.4964 9.15625 15.3594 9.15625C16.8695 9.15625 18.0938 7.93203 18.0938 6.42188C18.0938 4.97517 16.9702 3.79088 15.5481 3.69391Z"
                      fill="url(#paint0_linear_11_15756)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_11_15756"
                        x1="5.98438"
                        y1="0.5625"
                        x2="36.0625"
                        y2="39.625"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0.0721154" stop-color="#AD2EDB" />
                        <stop offset="1" stop-color="#039BF0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <div className="text-xl">
                  بالإضافة إلى التدريس المباشر من قبل المعلمين، تُشجع الجلسات
                  الحية على التفاعل بين الطلاب، مما يتيح لهم التعاون في المشاريع
                  والمشاركة في الأنشطة الجماعية في أي وقت.
                </div>
              </div>
            </Card>
          </motion.div>
        </section>

        {/* Metion */}
        <section className="bg-gradient-to-r from-blue-500 to-blue-600">
          <div className="space-y-10 max-w-7xl mx-auto px-4 py-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-white items-center">
              <div className="space-y-8 col-span-1">
                <div className="font-bold text-3xl">
                  مهمتنا هي إطلاق طاقات الإبداع في التعليم
                </div>
                <div className="text-lg">
                  نحن نؤمن أن التعليم الحقيقي يبدأ عندما تُمنح العقول المساحة
                  لتفكر بحرية وتبتكر. في منصتنا، نخلق بيئة تعليمية محفّزة تُزهر
                  فيها الأفكار، وتولد منها حلول ومهارات تغير المستقبل. نرعى
                  المواهب، وندعم الشغف، لنُخرج أفضل ما في كل متعلم.
                </div>
                <MainButton
                  title={"اكتشف رؤيتنا"}
                  size={"lg"}
                  onClick={() => navigate("/about-us")}
                />
              </div>
              <div className="col-span-1 lg:col-span-2 flex items-end justify-end">
                <div className="h-[440px] w-full lg:w-[700px]">
                  <img
                    loading="lazy"
                    src="metion.jpg"
                    alt="metion"
                    className="object-cover object-top h-full w-full rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Courses */}
        {courses.length !== 0 && (
          <section className="space-y-10 max-w-7xl mx-auto px-4">
            <SectionTitle title={"أشهر الدورات التعليمية"} />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loadingCategories
                ? Array(3)
                    .fill()
                    .map((_, index) => (
                      <Skeleton key={index} className="w-full h-40" />
                    ))
                : courses.map((course, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.2 }}
                    >
                      <CourseCard
                        item={course}
                        btnTitle={"التسجيل الآن"}
                        onClick={() =>
                          navigate("/course-info", { state: { course } })
                        }
                      />
                    </motion.div>
                  ))}
            </div>

            <div className="text-center">
              <MainButton
                title={"عرض كل الدورات التعليمية"}
                className={
                  "bg-transparent text-primary border-2 border-blue-600 shadow-sm"
                }
                Icon={ArrowUpLeft}
                onClick={() => navigate("/courses")}
              />
            </div>
          </section>
        )}

        {/* Teachers */}
        {teachers.length !== 0 && (
          <section className="space-y-10 max-w-7xl mx-auto px-4">
            <div className="space-y-4 text-center">
              <SectionTitle title={"نخبة من المعلمين المتخصصين"} />
              <p className="text-lg text-muted-foreground">
                مجموعة من المعلمين والمعلمات أصحاب الخبرة والشغف، يقدمون المعرفة
                بأساليب تعليمية حديثة وممتعة
              </p>
            </div>

            <TeacherCard
              teachers={teachers.slice(0, 4)}
              loading={loadingTeachers}
            />

            <div className="text-center">
              <MainButton
                title={"عرض كل المعلمون"}
                className={
                  "bg-transparent text-primary border-2 border-blue-600 shadow-sm"
                }
                Icon={ArrowUpLeft}
                onClick={() => navigate("/teachers")}
              />
            </div>
          </section>
        )}

        {/* Stories */}
        {reviews.length !== 0 && (
          <section className="space-y-10 max-w-7xl mx-auto px-4">
            <SectionTitle title={"قصص نجاح من أرض الواقع"} />
            <CustomCarousel data={reviews} loading={loadingReviews} />
          </section>
        )}

        {/* Register */}
        {!user && (
          <section className="py-52 relative space-y-10 px-4">
            {/* Background image */}
            <div className="absolute inset-0 -z-10">
              <img
                loading="lazy"
                src="banner.png"
                alt="Banner"
                className="object-cover w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-blue-500/90"></div>
            </div>
            <SectionTitle
              className={"text-white"}
              title={"كن أول من يعرف دوراتنا وعروضنا !"}
            />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!email) return;
                navigate(`/register?email=${email}`);
              }}
              className="bg-white p-2 rounded-xl flex items-center gap-4 w-full md:w-1/2 mx-auto"
            >
              <input
                type="email"
                placeholder="أدخل بريدك الإلكتروني"
                className="w-full px-4 py-2 focus:outline-none bg-transparent text-black"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                required
              />
              <MainButton type="submit" title={"اشترك الآن"} />
            </form>
          </section>
        )}

        {/* Why US */}
        <section className="space-y-10 max-w-7xl mx-auto px-4">
          <SectionTitle title={"لماذا تختار منصتنا التعليمية؟"} />
          <CustomAccordion items={accordingData} />
        </section>

        {/* Share */}
        <section className="bg-gradient-to-b from-blue-500 to-blue-700">
          <div className="space-y-10 max-w-7xl mx-auto px-4 py-20 text-white">
            <SectionTitle
              title={"شارك خبرتك... وكن جزءًا من مستقبل التعليم"}
              className={"text-white"}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-center">
              <div className="col-span-1 lg:col-span-2 space-y-4">
                <div className="text-xl font-medium">
                  هل تمتلك شغفًا بالتعليم وخبرة في مجالك؟
                </div>
                <div className="text-lg">
                  نحن في علي اكاديمي نبحث عن مدرّسين ملهمين مثلك، لنحدث فرقًا
                  حقيقيًا في حياة آلاف الطلاب حول العالم.
                </div>
                <div className="text-lg">
                  سواء كنت متخصصًا في اللغات، المناهج الدراسية، القرآن الكريم،
                  أو المهارات التقنية — منصتنا هي المكان المناسب لتشارك معرفتك
                  وتكسب دخلًا محترمًا من أي مكان في العالم.
                </div>
              </div>
              <div className="col-span-1 flex items-center justify-center">
                <img
                  src={"person2.jpg"}
                  alt={"person"}
                  className="object-cover object-top h-[325px] w-full lg:w-[282px] rounded-xl shadow-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {DiscoverData.map((industry, index) => (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="group relative"
                >
                  <CustomCard
                    className={"bg-white/80 hover:bg-white text-black"}
                    key={index}
                    name={industry.name}
                    description={industry.description}
                    Icon={industry.icon}
                  />
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <MainButton
                size={"lg"}
                title={"ابدأ معنا الان !"}
                onClick={() => navigate("/register")}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
